import { z } from 'zod';

/** Canonical widget type identifiers. Renderer uses these as registry keys. */
export const WIDGET_TYPES = {
  INSIGHTS_SUMMARY: 'insights_summary',
  PROJECT_STATUS: 'project_status',
  RECOMMENDATION: 'recommendation',
  PROJECT_CONFIDENCE: 'project_confidence',
  STAT_CARD: 'stat_card',
  AGENDA_ITEM: 'agenda_item',
  PROJECT_PLAN_STEPPER: 'project_plan_stepper',
  PROJECT_SNAPSHOT_CARD: 'project_snapshot_card',
  PROJECT_CARD: 'project_card',
  // PL-260517 design-handoff data-wiring additions. The matching dash-cased
  // strings (`research-snapshot` etc.) live in widget-layout-config.ts as
  // slot ids; the underscore-cased values here are the registry keys.
  //
  // Phase A rename: `research_snapshot` → `research_current_phase_snapshot`
  // and `research_bets_row` → `research_bets_snapshot`. See PL-260517 §2
  // (renaming impact). The old identifiers are deliberately not aliased so
  // any stale call sites surface as type errors.
  RESEARCH_CURRENT_PHASE_SNAPSHOT: 'research_current_phase_snapshot',
  RESEARCH_BETS_SNAPSHOT: 'research_bets_snapshot',
  RESEARCH_SEED_AND_SIDE: 'research_seed_and_side',
  INTERVIEW_PLAYBACK: 'interview_playback',
  BET_DETAIL: 'bet_detail',
  // PL-260517 Phase A — eighteen aggregator-addressable widgets registered
  // up-front so chat / MCP / hooks can rely on a stable union. Phase B
  // wires real loaders for each.
  PROJECT_HERO: 'project_hero',
  PROJECT_RECENT_ACTIVITY: 'project_recent_activity',
  FEATURED_RECOMMENDATION: 'featured_recommendation',
  AGENDA_OPEN_ITEMS: 'agenda_open_items',
  OVERVIEW_NEXT_INTERVIEW: 'overview_next_interview',
  RESEARCH_SEED_SECTION: 'research_seed_section',
  RESEARCH_CAPTURED_SIGNALS: 'research_captured_signals',
  RESEARCH_QUICK_ACTIONS: 'research_quick_actions',
  INTERVIEWS_UPCOMING_LIST: 'interviews_upcoming_list',
  INTERVIEWS_COMPLETED_LIST: 'interviews_completed_list',
  INSIGHTS_CONFIDENCE_TRAJECTORY: 'insights_confidence_trajectory',
  INSIGHTS_BETS_GRAPH: 'insights_bets_graph',
  RECOMMENDATIONS_LIST: 'recommendations_list',
  PARTICIPANTS_ICP_PANEL: 'participants_icp_panel',
  PARTICIPANTS_TABLE: 'participants_table',
  MEETINGS_LIVE_CARD: 'meetings_live_card',
  MEETINGS_UPCOMING_LIST: 'meetings_upcoming_list',
  MEETINGS_COMPLETED_LIST: 'meetings_completed_list',
  MEETINGS_KPI_STRIP: 'meetings_kpi_strip',
  // 022 — Chat thread widgets (presentational, no aggregator mapping)
  BET_SNAPSHOT: 'bet_snapshot',
  BETS_OVERVIEW: 'bets_overview',
  ASSUMPTION_SNAPSHOT: 'assumption_snapshot',
  DECISION_CARD: 'decision_card',
  PROJECT_ACTION: 'project_action',
  RECOMMENDATIONS_CARD: 'recommendations_card',
  INTERVIEWS_LIST: 'interviews_list',
  PROJECT_PLAN_SNAPSHOT: 'project_plan_snapshot',
  LINK_CARD: 'link_card',
  // 026 — Scoping widget types (presentational; no aggregator mapping)
  SC_LEARNT_CARD: 'sc_learnt_card',
  SC_CHOICE_CARD: 'sc_choice_card',
  SC_PARTICIPANT_TARGETS_CARD: 'sc_participant_targets_card',
  SC_RESEARCH_PREVIEW_CARD: 'sc_research_preview_card',
  SC_SCOPE_SUMMARY_CARD: 'sc_scope_summary_card',
  SC_ARTIFACT_CARD: 'sc_artifact_card',
  // 029 — demo closeout takeaway (extends the scope-summary pattern; rendered by
  // the demo page with page-level closeout handlers, not the generic registry).
  DEMO_SUMMARY_CARD: 'demo_summary_card',
} as const;
export type WidgetType = (typeof WIDGET_TYPES)[keyof typeof WIDGET_TYPES];
/** Alias used by routes/hooks. Same union as WidgetType. */
export type WidgetId = WidgetType;

export const InsightsSummaryPropsSchema = z.object({
  insights: z
    .array(
      z.object({
        label: z.string().describe('Insight name, e.g. "Market fit"'),
        confidence: z.number().min(0).max(1).describe('Confidence score 0–1'),
        evidenceCount: z.number().int().min(0).describe('Number of evidence items'),
        signalCount: z.number().int().min(0).describe('Number of raw signals'),
      }),
    )
    .min(1)
    .describe('Array of insight summary rows'),
});
export type InsightsSummaryProps = z.infer<typeof InsightsSummaryPropsSchema>;

export const ProjectStatusPropsSchema = z.object({
  title: z.string().describe('Status update title'),
  summary: z.string().describe('Brief summary text'),
  updatedAt: z.string().datetime().describe('ISO timestamp of last update'),
});
export type ProjectStatusProps = z.infer<typeof ProjectStatusPropsSchema>;

export const RecommendationPropsSchema = z.object({
  /** When set, transcript surfaces Accept/Decline that emit `client_structured`. */
  recommendation_id: z.string().uuid().optional().describe('Stable id matching project snapshot row'),
  title: z.string().describe('Recommendation headline'),
  body: z.string().describe('Detailed recommendation text'),
  priority: z.enum(['high', 'medium', 'low']).describe('Urgency level'),
});
export type RecommendationProps = z.infer<typeof RecommendationPropsSchema>;

export const ProjectConfidencePropsSchema = z.object({
  percentage: z.number().min(0).max(100).describe('Confidence percentage 0–100'),
  trend: z.enum(['up', 'down', 'stable']).describe('Direction of change'),
  delta: z.number().describe('Absolute change from previous period'),
});
export type ProjectConfidenceProps = z.infer<typeof ProjectConfidencePropsSchema>;

export const StatCardPropsSchema = z.object({
  label: z.string().describe('Metric name'),
  value: z.number().describe('Metric value'),
  trend: z.enum(['up', 'down', 'stable']).describe('Direction of change'),
  unit: z.string().optional().describe('Optional unit label'),
});
export type StatCardProps = z.infer<typeof StatCardPropsSchema>;

export const AgendaItemPropsSchema = z.object({
  title: z.string().describe('Agenda item title'),
  description: z.string().optional().describe('Optional detail text'),
  status: z.enum(['pending', 'active', 'completed']).describe('Current status'),
});
export type AgendaItemProps = z.infer<typeof AgendaItemPropsSchema>;

export const ProjectPlanStepperPropsSchema = z.object({
  phases: z
    .array(
      z.object({
        label: z.string().describe('Phase name'),
        status: z.enum(['completed', 'active', 'upcoming']).describe('Phase status'),
      }),
    )
    .min(1)
    .describe('Ordered list of project phases'),
  currentPhase: z.number().int().min(0).describe('Zero-based index of the active phase'),
});
export type ProjectPlanStepperProps = z.infer<typeof ProjectPlanStepperPropsSchema>;

export const ProjectSnapshotCardPropsSchema = z.object({
  variant: z
    .enum(['confidence', 'interviews', 'research', 'recommendations'])
    .describe('Card visual variant'),
  title: z.string().describe('Card title'),
  value: z.string().describe('Primary display value'),
  detail: z.string().optional().describe('Optional secondary text'),
});
export type ProjectSnapshotCardProps = z.infer<typeof ProjectSnapshotCardPropsSchema>;

export const ProjectCardPropsSchema = z.object({
  shortId: z.string().describe('Project short id for navigation'),
  name: z.string().describe('Project name'),
  type: z.string().optional().describe('Project type label (e.g., "Discovery")'),
  status: z.string().optional().describe('Phase or status label'),
  decisionIntent: z.string().optional().describe('What decision the project is informing'),
  progressSummary: z.string().optional().describe('Short progress hint, e.g., "3/8 interviews"'),
});
export type ProjectCardProps = z.infer<typeof ProjectCardPropsSchema>;

// ── PL-260517: Research / BetDetail / InterviewPlayback widget shapes ──────

/**
 * Research page hero — narrative + 4-card KPI cluster.
 * Backed by `projects.snapshot.ui.research.snapshot`.
 *
 * Phase A rename: previously `ResearchSnapshotPropsSchema`. The exported
 * symbol is preserved as an alias below for the duration of Phase B's
 * loader rollout; new code must use `ResearchCurrentPhaseSnapshotPayloadSchema`.
 */
export const ResearchCurrentPhaseSnapshotPayloadSchema = z.object({
  projectName: z.string(),
  phase: z.enum(['scoping', 'desk', 'participant', 'project', 'insights', 'delivery']),
  /** e.g. "ShiftSync research is 84% covered — 1 bet still soft." */
  headline: z.string(),
  /** Analyst sentence beneath the headline. */
  description: z.string(),
  seed: z.object({
    coveragePct: z.number().int().min(0).max(100),
    dimensionsTotal: z.number().int().min(0),
    dimensionsDone: z.number().int().min(0),
  }),
  interviews: z.object({
    total: z.number().int().min(0),
    upcoming: z.number().int().min(0),
    live: z.number().int().min(0),
  }),
  signals: z.object({
    total: z.number().int().min(0),
    weekDelta: z.number().int(),
  }),
  bets: z.object({
    verified: z.number().int().min(0),
    total: z.number().int().min(0),
    atRisk: z.number().int().min(0),
    rising: z.number().int().min(0),
  }),
});
export type ResearchCurrentPhaseSnapshotPayload = z.infer<typeof ResearchCurrentPhaseSnapshotPayloadSchema>;
/** @deprecated Use `ResearchCurrentPhaseSnapshotPayloadSchema`. */
export const ResearchSnapshotPropsSchema = ResearchCurrentPhaseSnapshotPayloadSchema;
/** @deprecated Use `ResearchCurrentPhaseSnapshotPayload`. */
export type ResearchSnapshotProps = ResearchCurrentPhaseSnapshotPayload;

/**
 * One bet card on the Research page bets row.
 * Mirrors the display fields persisted on `bets.snapshot` so the LLM and the
 * UI share a shape; the canonical definition is `BetSnapshot` in
 * `shared/bet-snapshot.ts` — this schema adds only the row-level identity
 * (`id`) that the snapshot blob doesn't need.
 */
export const BetCardPropsSchema = z.object({
  id: z.string(),
  title: z.string(),
  kicker: z.string(),
  category: z.string(),
  confidencePct: z.number().int().min(0).max(100),
  delta: z.number(),
  trend: z.enum(['up', 'down', 'flat']),
  level: z.enum(['hi', 'mid', 'lo']),
  seedCitations: z.number().int().min(0),
  signalCount: z.number().int().min(0),
  signalInterviews: z.number().int().min(0),
  risk: z.boolean(),
  rising: z.boolean(),
});
export type BetCardProps = z.infer<typeof BetCardPropsSchema>;

/**
 * Research bets row. Renders every active bet on the project — 0, 1, 3, 10
 * are all valid. Empty array surfaces the "no bets yet" empty state.
 *
 * Phase A rename: previously `ResearchBetsRowPropsSchema`. Old export
 * preserved as alias for PR-2 consumers; new code uses
 * `ResearchBetsSnapshotPayloadSchema`.
 */
export const ResearchBetsSnapshotPayloadSchema = z.object({
  bets: z.array(BetCardPropsSchema),
});
export type ResearchBetsSnapshotPayload = z.infer<typeof ResearchBetsSnapshotPayloadSchema>;
/** @deprecated Use `ResearchBetsSnapshotPayloadSchema`. */
export const ResearchBetsRowPropsSchema = ResearchBetsSnapshotPayloadSchema;
/** @deprecated Use `ResearchBetsSnapshotPayload`. */
export type ResearchBetsRowProps = ResearchBetsSnapshotPayload;

/**
 * One tile in the SeedSection matrix. Backed by a `research_seed` activity;
 * `activityId` is the bind back to the source row. The dimension list is
 * *not* a fixed catalogue — see PL-260517 §4.2.
 */
export const SeedDimensionTilePropsSchema = z.object({
  activityId: z.string().uuid(),
  /** activities.title */
  name: z.string(),
  /** activities.description */
  desc: z.string(),
  coveragePct: z.number().int().min(0).max(100),
  state: z.enum(['done', 'review', 'running', 'queued']),
  /** Derived from activities.output_summary. */
  finding: z.string(),
  sourceCount: z.number().int().min(0),
  /** Pre-formatted by the snapshot job: 'Feb 06' | 'live · 3 min ago' | 'queued'. */
  updatedAt: z.string(),
  flag: z.string().optional(),
});
export type SeedDimensionTileProps = z.infer<typeof SeedDimensionTilePropsSchema>;

/** Seed Report / Slide Deck card under the dimension matrix. */
export const OutputCardPropsSchema = z.object({
  kind: z.enum(['seed_report', 'slide_deck']),
  artefactId: z.string().uuid(),
  url: z.string().url(),
  updatedAt: z.string().datetime(),
  pages: z.number().int().min(0).optional(),
  version: z.string().optional(),
  sourceCount: z.number().int().min(0).optional(),
  citationCount: z.number().int().min(0).optional(),
  slideCount: z.number().int().min(0).optional(),
  chartCount: z.number().int().min(0).optional(),
});
export type OutputCardProps = z.infer<typeof OutputCardPropsSchema>;

/** Captured signal feed row on the Research side column. */
export const CapturedSignalPropsSchema = z.object({
  id: z.string(),
  /** Verbatim quote, italicised in the card. */
  quote: z.string(),
  source: z.enum(['interview', 'copilot-meeting', 'desk-research']),
  betTag: z.object({
    id: z.string(),
    label: z.string(),
  }),
  tone: z.enum(['positive', 'contradicting', 'informational']),
  /** Analyst note, ≤280 chars by convention. */
  whyThisMatters: z.string(),
});
export type CapturedSignalProps = z.infer<typeof CapturedSignalPropsSchema>;

/**
 * Composite slot: main column (SeedSection) + side column (CapturedSignals).
 *
 * @deprecated Split into `ResearchSeedSectionPayloadSchema` (seed dims +
 * artefact outputs) and `ResearchCapturedSignalsPayloadSchema` (just the
 * signals). Kept registered so any in-flight chat / MCP consumers don't
 * break; new aggregator loaders write the split shapes.
 */
export const ResearchSeedAndSidePropsSchema = z.object({
  dimensions: z.array(SeedDimensionTilePropsSchema),
  outputs: z.array(OutputCardPropsSchema).max(8),
  capturedSignals: z.array(CapturedSignalPropsSchema).max(5),
});
export type ResearchSeedAndSideProps = z.infer<typeof ResearchSeedAndSidePropsSchema>;

/** Seed Research surface: dimension tiles plus artefact output cards. */
export const ResearchSeedSectionPayloadSchema = z.object({
  dimensions: z.array(SeedDimensionTilePropsSchema),
  outputs: z.array(OutputCardPropsSchema).max(8),
});
export type ResearchSeedSectionPayload = z.infer<typeof ResearchSeedSectionPayloadSchema>;

/** Captured signals card on the Research side column (5 quote cards). */
export const ResearchCapturedSignalsPayloadSchema = z.object({
  signals: z.array(CapturedSignalPropsSchema).max(5),
});
export type ResearchCapturedSignalsPayload = z.infer<typeof ResearchCapturedSignalsPayloadSchema>;

/** One signal dot on the Interview Playback timeline. */
export const InterviewPlaybackSignalDotSchema = z.object({
  id: z.string(),
  timestampSec: z.number().int().min(0),
  speaker: z.object({
    name: z.string(),
    role: z.string(),
  }),
  importance: z.enum(['critical', 'high', 'medium', 'low']),
  quote: z.string(),
  analysis: z.string(),
  impactBadge: z.string(),
});
export type InterviewPlaybackSignalDot = z.infer<typeof InterviewPlaybackSignalDotSchema>;

/** Playback card on the Interviews page. */
export const InterviewPlaybackPropsSchema = z.object({
  interviewId: z.string().uuid(),
  interviewee: z.string(),
  company: z.string(),
  dateLabel: z.string(),
  durationSec: z.number().int().min(1),
  dots: z.array(InterviewPlaybackSignalDotSchema).max(40),
});
export type InterviewPlaybackProps = z.infer<typeof InterviewPlaybackPropsSchema>;

/**
 * Placeholder for the full Bet detail drilldown shape. The endpoint that
 * powers `BetDetailPage` returns a richer DTO defined in
 * `shared/bet-detail-schemas.ts` (lands in PR-2 alongside the route). This
 * registry entry exists now so the Phase 13c MCP catalogue can advertise
 * the surface as soon as it ships.
 */
export const BetDetailPropsSchema = BetCardPropsSchema;
export type BetDetailProps = z.infer<typeof BetDetailPropsSchema>;

// ── PL-260517 Phase A — eighteen aggregator widget payload schemas ─────────
// Each schema mirrors the corresponding component's existing props
// interface. Optional fields stay optional so the loaders can degrade to a
// minimum viable payload. Phase B fills in real loader output.

/** ProjectHero — phase pill, day counter, confidence ring, 3 metric chips. */
export const ProjectHeroMetricSchema = z.object({
  value: z.string(),
  label: z.string(),
  sub: z.string().optional(),
  /** Lucide icon name; renderer maps name -> LucideIcon via a phase-B map. */
  icon: z.string(),
  color: z.string(),
});
export const ProjectHeroPayloadSchema = z.object({
  projectName: z.string(),
  phaseLabel: z.string(),
  dayCounter: z.string(),
  description: z.string(),
  confidencePct: z.number().min(0).max(100),
  confidenceDelta: z.string(),
  metrics: z.array(ProjectHeroMetricSchema).optional(),
});
export type ProjectHeroPayload = z.infer<typeof ProjectHeroPayloadSchema>;

/** Recent activity stream item (signal/interview/risk/report). */
export const RecentActivityItemSchema = z.object({
  kind: z.enum(['signal', 'interview', 'risk', 'report']),
  tone: z.enum(['good', 'warn', 'mid', 'info']),
  when: z.string(),
  title: z.string(),
  body: z.string(),
  tags: z.array(z.string()),
});
export const ProjectRecentActivityPayloadSchema = z.object({
  items: z.array(RecentActivityItemSchema),
});
export type ProjectRecentActivityPayload = z.infer<typeof ProjectRecentActivityPayloadSchema>;

/** Top recommendation surfaced on Overview. */
export const FeaturedRecommendationPayloadSchema = z.object({
  recommendationId: z.string().optional(),
  title: z.string(),
  description: z.string(),
  /** Lucide icon name; renderer maps name -> LucideIcon via a phase-B map. */
  icon: z.string(),
  label: z.string().optional(),
  ctaLabel: z.string().optional(),
  prominent: z.boolean().optional(),
});
export type FeaturedRecommendationPayload = z.infer<typeof FeaturedRecommendationPayloadSchema>;

/** Open agenda items ("Open with Anaka" prompts). */
export const AgendaOpenItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  when: z.string(),
});
export const AgendaOpenItemsPayloadSchema = z.object({
  items: z.array(AgendaOpenItemSchema),
});
export type AgendaOpenItemsPayload = z.infer<typeof AgendaOpenItemsPayloadSchema>;

/** Up-next interview card on Overview. */
export const OverviewNextInterviewPayloadSchema = z.object({
  participantName: z.string(),
  role: z.string().optional(),
  whenLabel: z.string(),
  confidence: z.number().min(0).max(100),
  ctaLabel: z.string().optional(),
});
export type OverviewNextInterviewPayload = z.infer<typeof OverviewNextInterviewPayloadSchema>;

/** Research quick-actions / kickoff card — pure CTA, no data. */
export const ResearchQuickActionsPayloadSchema = z.object({}).strict();
export type ResearchQuickActionsPayload = z.infer<typeof ResearchQuickActionsPayloadSchema>;

/** Upcoming interview row. */
export const UpcomingInterviewSchema = z.object({
  id: z.string(),
  participantName: z.string(),
  participantInitials: z.string(),
  company: z.string(),
  topic: z.string(),
  /** Human-readable display string, e.g. "Mon 16 Mar · 14:00". Used for accessibility / screen-reader text. */
  scheduledFor: z.string(),
  startsIn: z.string(),
  /** Numeric day-of-month as a string, e.g. "16". Used by the calendar badge. */
  scheduledDayNumber: z.string(),
  /** Short month abbreviation, e.g. "Mar". Used by the calendar badge. */
  scheduledMonthLabel: z.string(),
  /** Short weekday abbreviation, e.g. "Mon". Used by the calendar badge. */
  scheduledWeekday: z.string(),
});
export const InterviewsUpcomingListPayloadSchema = z.object({
  interviews: z.array(UpcomingInterviewSchema),
});
export type InterviewsUpcomingListPayload = z.infer<typeof InterviewsUpcomingListPayloadSchema>;

/** Completed interview row. */
export const CompletedInterviewSchema = z.object({
  id: z.string(),
  participantName: z.string(),
  participantInitials: z.string(),
  completedAt: z.string(),
});
export const InterviewsCompletedListPayloadSchema = z.object({
  interviews: z.array(CompletedInterviewSchema),
});
export type InterviewsCompletedListPayload = z.infer<typeof InterviewsCompletedListPayloadSchema>;

/** Insights confidence trajectory chart. */
export const ConfidenceTrajectoryPointSchema = z.object({
  label: z.string(),
  value: z.number().min(0).max(100),
});
export const InsightsConfidenceTrajectoryPayloadSchema = z.object({
  current: z.number().min(0).max(100),
  deltaPct: z.number(),
  description: z.string().optional(),
  points: z.array(ConfidenceTrajectoryPointSchema),
});
export type InsightsConfidenceTrajectoryPayload = z.infer<typeof InsightsConfidenceTrajectoryPayloadSchema>;

/** Insights bets graph (bet → assumption → evidence → signal). */
export const InsightBetSummarySchema = z.object({
  id: z.string(),
  confidence: z.number().min(0).max(100),
  deltaPct: z.number(),
  theme: z.string().optional(),
  title: z.string(),
  summary: z.string().optional(),
  evidenceCount: z.number().int().min(0),
  signalCount: z.number().int().min(0),
  interviewCount: z.number().int().min(0),
  emerging: z.boolean().optional(),
  risk: z.boolean().optional(),
});
export const InsightsBetsGraphPayloadSchema = z.object({
  bets: z.array(InsightBetSummarySchema),
});
export type InsightsBetsGraphPayload = z.infer<typeof InsightsBetsGraphPayloadSchema>;

/** Recommendations list card data. */
export const RecommendationListItemSchema = z.object({
  id: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
  theme: z.string(),
  title: z.string(),
  summary: z.string(),
  /** Pre-formatted, e.g. "68% → 78%". */
  confidence: z.string(),
  signalCount: z.number().int().min(0),
  interviewCount: z.number().int().min(0),
  detailHref: z.string().optional(),
});
export const RecommendationsListPayloadSchema = z.object({
  recommendations: z.array(RecommendationListItemSchema),
});
export type RecommendationsListPayload = z.infer<typeof RecommendationsListPayloadSchema>;

/** ICP criterion row on Participants. */
export const ICPCriterionPayloadSchema = z.object({
  /** Lucide icon name; renderer maps name -> LucideIcon via a phase-B map. */
  icon: z.string(),
  category: z.string(),
  title: z.string(),
  description: z.string(),
});
export const ParticipantsIcpPanelPayloadSchema = z.object({
  criteria: z.array(ICPCriterionPayloadSchema),
  subtitle: z.string().optional(),
});
export type ParticipantsIcpPanelPayload = z.infer<typeof ParticipantsIcpPanelPayloadSchema>;

/** Participant row (table). */
export const ParticipantSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  initial: z.string(),
  companyName: z.string(),
  companyDetail: z.string(),
  source: z.string(),
  fitLevel: z.enum(['high', 'medium', 'low', 'excluded']),
  fitScore: z.number().int().min(0).max(100),
  interviewCount: z.number().int().min(0),
});
export const ParticipantsTablePayloadSchema = z.object({
  participants: z.array(ParticipantSchema),
});
export type ParticipantsTablePayload = z.infer<typeof ParticipantsTablePayloadSchema>;

/** Meetings live card — at most one in-progress meeting. */
export const MeetingsLiveCardPayloadSchema = z.object({
  meetingId: z.string(),
  title: z.string(),
  hostName: z.string(),
  hostInitials: z.string(),
  attendeeCount: z.number().int().min(0),
  provider: z.string(),
  /** Elapsed time, e.g. "00:14:08". */
  elapsedLabel: z.string(),
});
export type MeetingsLiveCardPayload = z.infer<typeof MeetingsLiveCardPayloadSchema>;

/** Upcoming meeting tile. */
export const UpcomingMeetingSchema = z.object({
  id: z.string(),
  title: z.string(),
  participantName: z.string(),
  participantInitials: z.string(),
  organisation: z.string(),
  scheduledFor: z.string(),
  startsIn: z.string(),
});
export const MeetingsUpcomingListPayloadSchema = z.object({
  meetings: z.array(UpcomingMeetingSchema),
});
export type MeetingsUpcomingListPayload = z.infer<typeof MeetingsUpcomingListPayloadSchema>;

/** Completed meeting tile. */
export const CompletedMeetingSchema = z.object({
  id: z.string(),
  title: z.string(),
  participantInitials: z.string(),
  completedAt: z.string(),
});
export const MeetingsCompletedListPayloadSchema = z.object({
  meetings: z.array(CompletedMeetingSchema),
});
export type MeetingsCompletedListPayload = z.infer<typeof MeetingsCompletedListPayloadSchema>;

/** Meetings KPI strip. Mirrors `KpiCard` shapes — numeric/string value union. */
export const MeetingsKpiSchema = z.object({
  /** Stable identifier (e.g. 'upcoming'). */
  key: z.string(),
  label: z.string(),
  value: z.union([z.string(), z.number()]),
  /** KpiCard variant token. */
  variant: z.string(),
  /** Lucide icon name; renderer maps name -> LucideIcon via a phase-B map. */
  icon: z.string(),
  iconClassName: z.string().optional(),
});
export const MeetingsKpiStripPayloadSchema = z.object({
  kpis: z.array(MeetingsKpiSchema),
});
export type MeetingsKpiStripPayload = z.infer<typeof MeetingsKpiStripPayloadSchema>;

// =============================================================================
// 022 — Chat thread widget schemas (presentational; no aggregator mapping)
// =============================================================================

const ConfidenceLvlSchema = z.enum(['hi', 'mid', 'lo']);
const TrendDirectionSchema = z.enum(['up', 'down', 'flat']);

/**
 * The curated set of Lucide icon names a chat-thread widget may specify.
 * **Icons are Lucide-only — never emoji.** This list is the single source of
 * truth the prompt layers reference so the LLM knows exactly which icon names
 * are valid for `decision_card` / `project_action` / `link_card`. The client
 * maps each name to its `lucide-react` component in
 * `src/components/v2/widgets/widget-icons.ts` (kept in sync by a typed
 * `Record<WidgetIconName, LucideIcon>`).
 */
export const WIDGET_ICON_NAMES = [
  'Target', 'Flag', 'Rocket', 'Lightbulb', 'Zap', 'TrendingUp', 'TrendingDown',
  'Clock', 'LoaderCircle', 'Gavel', 'Compass', 'Sparkles', 'Star', 'Trophy',
  'Search', 'Users', 'Mic', 'FileText', 'ListChecks', 'Activity', 'Calendar',
  'GitBranch', 'LayoutGrid', 'Building2', 'ArrowUpRight', 'CircleCheck', 'CircleAlert',
] as const;
export type WidgetIconName = (typeof WIDGET_ICON_NAMES)[number];

/**
 * Optional Lucide-icon field. Unknown/legacy values (e.g. an emoji) degrade to
 * `undefined` via `.catch` so a bad icon never fails the whole widget — the
 * component falls back to its contextual default icon.
 */
const WidgetIconField = z
  .enum(WIDGET_ICON_NAMES)
  .describe(`Lucide icon name. One of: ${WIDGET_ICON_NAMES.join(', ')}`)
  .optional()
  .catch(undefined);

export const BetSnapshotPropsSchema = z.object({
  title: z.string(),
  summary: z.string().optional(),
  tag: z.string().optional(),
  icon: z.string().optional(),
  confidence: z.number().min(0).max(100),
  lvl: ConfidenceLvlSchema,
  trend: z.number(),
  sparkline: z.array(z.number()).optional(),
  sessions: z.number().int().nonnegative().optional(),
  signals: z.number().int().nonnegative().optional(),
  interviews: z.number().int().nonnegative().optional(),
  assumptions: z
    .array(
      z.object({
        text: z.string(),
        confidence: z.number().min(0).max(100),
        lvl: ConfidenceLvlSchema,
      }),
    )
    .optional(),
});
export type BetSnapshotProps = z.infer<typeof BetSnapshotPropsSchema>;

export const BetsOverviewPropsSchema = z.object({
  title: z.string().optional(),
  bets: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      confidence: z.number().min(0).max(100),
      lvl: ConfidenceLvlSchema,
      trend: TrendDirectionSchema,
      evidence: z.number().int().nonnegative(),
      signals: z.number().int().nonnegative(),
    }),
  ),
});
export type BetsOverviewProps = z.infer<typeof BetsOverviewPropsSchema>;

export const AssumptionSnapshotPropsSchema = z.object({
  title: z.string(),
  summary: z.string().optional(),
  confidence: z.number().min(0).max(100),
  lvl: ConfidenceLvlSchema,
  trend: z.number(),
  sparkline: z.array(z.number()).optional(),
  interviews: z.number().int().nonnegative().optional(),
  signals: z.number().int().nonnegative().optional(),
});
export type AssumptionSnapshotProps = z.infer<typeof AssumptionSnapshotPropsSchema>;

export const DecisionCardPropsSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  icon: WidgetIconField,
  variant: z.enum(['action', 'go']).optional(),
  accent: z.string().optional(),
});
export type DecisionCardProps = z.infer<typeof DecisionCardPropsSchema>;

/** Milestone state — drives the ProjectAction accent colour + default icon. */
export const ProjectActionStateSchema = z.enum(['action_required', 'in_progress', 'reached']);
export type ProjectActionState = z.infer<typeof ProjectActionStateSchema>;

export const ProjectActionPropsSchema = z.object({
  title: z.string(),
  /** Milestone state: action_required (orange) · in_progress (blue) · reached (green). */
  state: ProjectActionStateSchema.optional().catch(undefined),
  kicker: z.string().optional(),
  body: z.string().optional(),
  icon: WidgetIconField,
  progress: z
    .object({
      pct: z.number().min(0).max(100),
      label: z.string().optional(),
    })
    .optional(),
  recap: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .optional(),
});
export type ProjectActionProps = z.infer<typeof ProjectActionPropsSchema>;

export const RecommendationsCardPropsSchema = z.object({
  recs: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        why: z.string().optional(),
        priority: z.enum(['high', 'med', 'low']),
        steps: z
          .array(z.object({ label: z.string(), action: z.string() }))
          .optional(),
      }),
    )
    .max(3),
  total: z.number().int().nonnegative().optional(),
  navTo: z.string().optional(),
});
export type RecommendationsCardProps = z.infer<typeof RecommendationsCardPropsSchema>;

export const InterviewsListPropsSchema = z.object({
  mode: z.enum(['planned', 'completed']),
  items: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      org: z.string().optional(),
      scheduledAt: z.string().optional(),
      tag: z.string().optional(),
      tagColor: z.string().optional(),
      role: z.string().optional(),
      summary: z.string().optional(),
      signalsCount: z.number().int().nonnegative().optional(),
    }),
  ),
});
export type InterviewsListProps = z.infer<typeof InterviewsListPropsSchema>;

export const ProjectPlanSnapshotPropsSchema = z.object({
  badge: z.string().optional(),
  phases: z.array(
    z.object({
      number: z.number().int().positive(),
      name: z.string(),
      state: z.enum(['done', 'active', 'upcoming']),
      note: z.string().optional(),
    }),
  ),
});
export type ProjectPlanSnapshotProps = z.infer<typeof ProjectPlanSnapshotPropsSchema>;

export const LinkCardPropsSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  icon: WidgetIconField,
  label: z.string().optional(),
  navTo: z.string(),
  accent: z.string().optional(),
});
export type LinkCardProps = z.infer<typeof LinkCardPropsSchema>;

// ── 026 — Scoping widget schemas ──────────────────────────────────────────────

/**
 * 2026-07-25 D5 (004-p9 T059, orient redesign) — `statements[]` is the preferred shape:
 * one card item per PILLAR, carrying every finding for that pillar as its own statement
 * with its own `exploring` flag — grouping must never conflate a confirmed finding and a
 * speculative one under a single, undifferentiated badge. `text`/`exploring` stay on the
 * item for back-compat with pre-grouping producers (Storybook fixtures, any legacy
 * model-emitted card) — `.superRefine` requires at least one of the two shapes.
 */
export const ScLearntCardPropsSchema = z.object({
  items: z.array(z.object({
    pillar: z.string(),
    icon: z.string().optional(),
    source: z.string().optional(),
    text: z.string().optional(),
    /** ANA-811 — below FR-018's earned-confidence floor: render as a hypothesis to
     *  test, never as a stated fact. Back-compat single-statement shape only. */
    exploring: z.boolean().optional(),
    /** Preferred shape (2026-07-25 D5) — every finding for this pillar, each with its
     *  own earned/exploring status. */
    statements: z.array(z.object({
      text: z.string(),
      exploring: z.boolean().optional(),
    })).min(1).optional(),
  }).superRefine((item, ctx) => {
    if (!item.text && !item.statements) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'item requires text or statements[]' });
    }
  })).min(1),
});
export type ScLearntCardProps = z.infer<typeof ScLearntCardPropsSchema>;

export const ScChoiceCardPropsSchema = z.object({
  title: z.string(),
  prompt: z.string().optional(),
  options: z.array(z.object({
    id: z.string(),
    icon: z.string().optional(),
    label: z.string(),
    sub: z.string().optional(),
  })).min(2),
  defaultId: z.string().optional(),
  confirmLabel: z.string().optional(),
  // Optional draft/confirmed state badge: true → DRAFT, false → CONFIRMED, omitted → no badge.
  draft: z.boolean().optional(),
});
export type ScChoiceCardProps = z.infer<typeof ScChoiceCardPropsSchema>;

export const ScParticipantTargetsCardPropsSchema = z.object({
  targets: z.array(z.object({
    role: z.string(),
    segment: z.string(),
    count: z.number().int().min(0),
    icon: z.string().optional(),
    tag: z.string().optional(),
    tone: z.string().optional(),
  })).min(1),
  total: z.number().int().min(0).optional(),
  // Optional draft/confirmed state badge: true → DRAFT, false → CONFIRMED, omitted → no badge.
  draft: z.boolean().optional(),
});
export type ScParticipantTargetsCardProps = z.infer<typeof ScParticipantTargetsCardPropsSchema>;

export const ScResearchPreviewCardPropsSchema = z.object({
  areas: z.array(z.object({
    icon: z.string(),
    label: z.string(),
    sub: z.string().optional(),
    source: z.string().optional(),
  })).min(1),
  estimate: z.string().optional(),
  // Optional draft/confirmed state badge: true → DRAFT, false → CONFIRMED, omitted → no badge.
  draft: z.boolean().optional(),
});
export type ScResearchPreviewCardProps = z.infer<typeof ScResearchPreviewCardPropsSchema>;

/**
 * Coerce common near-miss `sc_scope_summary_card` emissions to the strict shape
 * BEFORE validation (mirrors `normalizeScArtifactData`). The lock gate is the
 * highest-stakes moment, so an imperfect LLM emission should degrade to a
 * rendered (if sparse) summary, not a FallbackWidget error card. Handles:
 * snake_case aliases, bare-string / typed-capture bets, and goal objects.
 */
function normalizeScScopeSummaryData(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const data = raw as Record<string, unknown>;
  const obj = (x: unknown): Record<string, unknown> =>
    x && typeof x === 'object' ? (x as Record<string, unknown>) : {};
  const str = (x: unknown): string | undefined => (typeof x === 'string' && x.trim() ? x : undefined);
  const out: Record<string, unknown> = { ...data };

  // snake_case aliases → camelCase.
  if (out.projectName === undefined) out.projectName = str(data.project_name) ?? out.projectName;
  if (out.decisionType === undefined) out.decisionType = str(data.decision_type) ?? out.decisionType;
  // Enum is capitalised; coerce case + the demo "Exploration" synonym.
  if (typeof out.decisionType === 'string') {
    const d = out.decisionType.toLowerCase();
    if (d === 'validation') out.decisionType = 'Validation';
    else if (d === 'exploratory' || d === 'exploration') out.decisionType = 'Exploratory';
  }

  // bets: bare string | { text | statement, assumptionCount | assumption_count | assumptions[] }.
  if (Array.isArray(data.bets)) {
    out.bets = data.bets
      .map((b): Record<string, unknown> | null => {
        if (typeof b === 'string') return str(b) ? { text: b } : null;
        const o = obj(b);
        const text = str(o.text) ?? str(o.statement);
        if (!text) return null;
        const count =
          typeof o.assumptionCount === 'number'
            ? o.assumptionCount
            : typeof o.assumption_count === 'number'
              ? o.assumption_count
              : Array.isArray(o.assumptions)
                ? o.assumptions.length
                : undefined;
        return { text, ...(count !== undefined ? { assumptionCount: count } : {}) };
      })
      .filter((x): x is Record<string, unknown> => x !== null);
  }

  // goals: string | { name | text | statement }.
  if (Array.isArray(data.goals)) {
    out.goals = data.goals
      .map((g): string | null => (typeof g === 'string' ? (str(g) ?? null) : (str(obj(g).name) ?? str(obj(g).text) ?? str(obj(g).statement) ?? null)))
      .filter((x): x is string => x !== null);
  }

  return out;
}

export const ScScopeSummaryCardPropsSchema = z.preprocess(normalizeScScopeSummaryData, z.object({
  projectName: z.string().describe('The project name — shown as the card title.'),
  decisionType: z
    .enum(['Validation', 'Exploratory'])
    .optional()
    .describe('The project type — "Validation" or "Exploratory" — shown as a pill in the header.'),
  decision: z
    .string()
    .optional()
    .describe('The decision intent — the specific decision this scope has to land. Shown as the sub-line under the project name (NOT a generic description).'),
  bets: z
    .array(
      z.object({
        text: z.string().describe('The bet statement.'),
        assumptionCount: z
          .number()
          .int()
          .min(0)
          .optional()
          .describe('How many assumptions were captured under this bet (a count, shown beside the bet).'),
      }),
    )
    .optional()
    .default([])
    .describe('The bets in the locked scope, each with the number of assumptions testing it — a substance rollup, not bare totals. List every bet.'),
  goals: z
    .array(z.string())
    .optional()
    .default([])
    .describe('The learning-goal names in the scope.'),
  draft: z
    .boolean()
    .optional()
    .describe('Lifecycle state badge: true → DRAFT, false → CONFIRMED, omitted → no badge.'),
}))
  .describe(
    'Scope summary card shown at the lock gate — the full-scope review the founder approves. Title = projectName; sub-line = the decision intent; header shows the project-type pill; body lists the bets (each with its assumption count) and the learning goals. Emit it when the scope is ready to lock.',
  );
export type ScScopeSummaryCardProps = z.infer<typeof ScScopeSummaryCardPropsSchema>;

// 029 — DemoSummaryCard payload (snake_case as emitted by the demo dispatcher).
// Demo's own decision-type enum ('Exploration'|'Validation'); the card renders it
// directly (not the scoping 'Exploratory' label).
export const DemoSummaryCardPropsSchema = z.object({
  title: z.string(),
  decision_type: z.enum(['Exploration', 'Validation']).optional(),
  decision: z.string(),
  bets_count: z.number().int().min(0),
  assumptions_count: z.number().int().min(0),
  approach: z.string(),
  next_steps: z.string().optional(),
});
export type DemoSummaryCardProps = z.infer<typeof DemoSummaryCardPropsSchema>;

const ScArtifactDecisionPayload = z.object({
  kind: z.literal('decision'),
  intent: z.string().describe(
    "The decision or question the user wants this project to resolve, stated in the user's own words.",
  ),
  projectType: z
    .enum(['Validation', 'Exploratory'])
    .optional()
    .describe(
      'Whether the work is primarily Validation (testing a specific belief) or Exploratory (open discovery).',
    ),
  rationale: z
    .string()
    .optional()
    .describe('Why this is the decision that matters now — the context or driver behind it.'),
});

const ScArtifactBetsPayload = z.object({
  kind: z.literal('bets'),
  items: z
    .array(
      z.object({
        text: z
          .string()
          .describe(
            'A single bet — a belief the user is treating as load-bearing for the decision, in the way the user framed it.',
          ),
        // Captured `bet_type` (e.g. "Desirability") — surfaced as a humanised type
        // pill, exactly like `assumption_type` on an assumption. Free-form string
        // (resolved to an enum at promotion). Kept DISTINCT from `provenance` so the
        // pill shows the type, not the rationale.
        type: z
          .string()
          .optional()
          .describe('The kind of bet (free-form, e.g. Desirability or Value); shown as a pill.'),
        provenance: z
          .string()
          .optional()
          .describe('Where this bet came from — e.g. something the user said, or the rationale behind it.'),
      }),
    )
    .describe('The bets discussed so far. Only include bets the user has actually engaged with.'),
});

const ScArtifactAssumptionsPayload = z.object({
  kind: z.literal('assumptions'),
  groups: z
    .array(
      z.object({
        bet: z.string().describe('The parent bet these assumptions sit under.'),
        items: z.array(
          z.object({
            text: z.string().describe('A single assumption the parent bet depends on being true.'),
            // Captured `assumption_type` (e.g. "TrustBoundaries") — surfaced as a
            // humanised type pill. Free-form string (resolved to an enum at promotion).
            type: z
              .string()
              .optional()
              .describe('The kind of assumption (free-form, e.g. a trust-boundary or demand assumption); shown as a pill.'),
            // Captured `risk_level` — the meaningful risk signal (replaces the retired
            // validated/partly/net-new evidence axis). Rendered as a colour-coded chip.
            risk: z
              .enum(['low', 'medium', 'high', 'critical'])
              .optional()
              .describe('How costly it would be if this assumption turned out to be wrong.'),
            // 029 FR-010b — demo risk flag: true = riskiest to test (load-bearing AND
            // unproven) → amber dot; absent/false = likely-holds → green dot.
            weak: z
              .boolean()
              .optional()
              .describe('True if this is among the riskiest to test — load-bearing for the bet and still unproven.'),
          }),
        ),
      }),
    )
    .describe('Assumptions grouped under the bet each one supports.'),
});

// The unified "bets with assumptions nested" card (bets↔assumptions-merge plan, MF1).
// Bets are the top-level item; each carries its own type/provenance + draft/confirmed
// status and the riskiest assumptions nested beneath it. A NEW discriminator literal
// (`bets_with_assumptions`) — NOT a rename of `bets` (two members can't share a literal
// in a Zod discriminatedUnion; that throws at module load). The old flat `bets` and
// grouped `assumptions` members stay in the union so persisted/hydrated cards still render.
const ScArtifactBetsWithAssumptionsPayload = z.object({
  kind: z.literal('bets_with_assumptions'),
  groups: z
    .array(
      z.object({
        // ANA-936 — the stable Cloud-minted roster id for this bet (RosterItem.id,
        // e.g. "bet_7"), threaded through so the client can key state (expand/
        // collapse, list keys) on IDENTITY instead of the rendered bet text — a
        // reword no longer orphans client-side state. Additive + optional: the 6a
        // briefing_draft source has no roster id (legitimately absent, not an
        // oversight — see materialize-cards.ts buildBetsWithAssumptionsData), and a
        // card persisted/hydrated before this field existed carries none either;
        // both cases still render via the pre-existing content-derived fallback.
        // In practice Cloud-set: `materializeCardsFromRoster` is the only writer
        // that populates it today. But UNLIKE `titleOverride` on
        // ScArtifactCardPropsSchema, this field is NOT hidden from
        // scArtifactCardSchemaForKind — `sc_artifact_card` sits in
        // CHAT_THREAD_WIDGET_IDS (the LLM-emitted set `widget_catalog` and the
        // baked per-section schema both hand to the model), so a model-authored
        // tool call could in principle set it too (pr-review P2). `.min(1)` below
        // rejects an empty string at the wire; uniqueness is NOT schema-enforced
        // (Zod has no cross-item constraint here), so the client additionally
        // treats a duplicate id as untrusted rather than relying on this field
        // being well-formed — see the second `contentKeys` pass in
        // `BetsWithAssumptionsBody`, ScArtifactCard.tsx.
        id: z.string().min(1).optional().describe('Stable identity for this bet — internal, not for display.'),
        bet: z.string().describe('The bet statement — the group header.'),
        // The bet's own type (e.g. Desirability) and origin — shown as pills on the header,
        // mirroring the flat `bets` card. Kept distinct: `type` = the pill, `provenance` = origin.
        type: z.string().optional().describe('The kind of bet (free-form, e.g. Desirability); shown as a pill.'),
        provenance: z.string().optional().describe('Where this bet came from — something the user said, or its rationale.'),
        // Per-bet lifecycle — flips to confirmed when the user agrees the bet AND its
        // assumptions are right (MF4). Omitted → no badge.
        status: z.enum(['draft', 'confirmed']).optional().describe('Per-bet lifecycle: draft (working) or confirmed (agreed).'),
        // MF2 — may be EMPTY: a bet is captured and shown BEFORE its assumptions are mapped.
        assumptions: z
          .array(
            z.object({
              // ANA-936 — same contract as the group `id` above (incl. the `.min(1)` +
              // client-side duplicate handling — pr-review P2), for this assumption's own roster id.
              id: z.string().min(1).optional().describe('Stable identity for this assumption — internal, not for display.'),
              text: z.string().describe('A single assumption the parent bet depends on being true.'),
              type: z.string().optional().describe('The kind of assumption (free-form); shown as a pill.'),
              risk: z
                .enum(['low', 'medium', 'high', 'critical'])
                .optional()
                .describe('How costly it would be if this assumption turned out wrong.'),
              weak: z.boolean().optional().describe('True if among the riskiest to test — load-bearing and still unproven.'),
            }),
          )
          .describe('The riskiest assumptions under this bet. MAY be empty (a bet captured before its assumptions).'),
      }),
    )
    .describe('Each bet with its assumptions nested beneath, in capture order.'),
});

const ScArtifactGoalsPayload = z.object({
  kind: z.literal('goals'),
  // Enriched from a bare string list to carry the captured `goal_type` for the
  // type pill. `normalizeScArtifactData` coerces legacy `string[]` (and the typed
  // `{name, goal_type}` wire shape) into this shape before validation.
  items: z
    .array(
      z.object({
        text: z
          .string()
          .describe('A single learning goal — what the user wants to learn or the evidence they want to gather.'),
        type: z.string().optional().describe('The kind of goal (free-form; shown as a pill).'),
      }),
    )
    .describe('The learning goals agreed with the user. Build these from what the user says, not from research.'),
});

const ScArtifactScopePayload = z.object({
  kind: z.literal('scope'),
  summary: z.string().optional().describe('A short summary of the agreed scope for this project.'),
  items: z.array(z.string()).optional().describe('The individual scope points that make up the summary.'),
});

const ScArtifactNotePayload = z.object({
  kind: z.literal('note'),
  text: z.string().describe('A brief note captured during the conversation.'),
});

/**
 * 037 — coerce a typed-capture `sc_artifact_card` payload back to the card shape.
 *
 * The scoping guide instructs BOTH "emit the card" (card shape: bets `items[].text`,
 * assumptions `groups[].items[].text`) AND "capture typed" (`statement` / `bet_type` /
 * embedded `assumptions`). The model conflates them and sometimes emits the card
 * `data` with the TYPED field names, which fails the strict discriminated union →
 * `WidgetRenderer` drops the card to a `FallbackWidget`. This normalizes the typed
 * variants to the card shape BEFORE validation (mirrors `normalizeWidgetActions`
 * id→action). Idempotent on already-card-shaped payloads; decision/goals/scope/note
 * payloads pass through untouched.
 */
function normalizeScArtifactData(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const data = raw as Record<string, unknown>;
  if (!data.payload || typeof data.payload !== 'object') return raw;
  const p = data.payload as Record<string, unknown>;

  const obj = (x: unknown): Record<string, unknown> =>
    x && typeof x === 'object' ? (x as Record<string, unknown>) : {};
  const textOf = (x: unknown): string | undefined => {
    if (typeof x === 'string') return x;
    const o = obj(x);
    if (typeof o.text === 'string') return o.text;
    if (typeof o.statement === 'string') return o.statement;
    return undefined;
  };
  const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);

  // A bare status token (the draft/confirmed state) must never masquerade as an
  // item's text — the model sometimes drops it into the items array, rendering a
  // phantom "draft" bet/assumption/goal line (issue 3).
  const isStatusToken = (s: string): boolean => {
    const t = s.trim().toLowerCase();
    return t === 'draft' || t === 'confirmed';
  };
  // Interpret a mis-placed status value (boolean or 'draft'/'confirmed' string) as
  // the top-level draft flag.
  const statusToDraft = (v: unknown): boolean | undefined => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const t = v.trim().toLowerCase();
      if (t === 'draft') return true;
      if (t === 'confirmed') return false;
    }
    return undefined;
  };
  // Lift a draft/status that landed inside the payload (or on `data.status`) up to
  // the canonical top-level `draft` flag — only when it isn't already set (issue 3).
  const draftPatch: Record<string, unknown> =
    data.draft === undefined
      ? (() => {
          const d = statusToDraft(p.draft) ?? statusToDraft(p.status) ?? statusToDraft(data.status);
          return d !== undefined ? { draft: d } : {};
        })()
      : {};

  // Coerce ONLY the known typed-shape drift: statement→text, bet_type→type,
  // rationale→provenance, embedded/flat→groups. Every other field (weak, …) passes
  // through VERBATIM so the strict inner schema still validates enums (a bad status
  // is still rejected, not silently dropped).
  if (p.kind === 'bets') {
    const items = list(p.items)
      .map((it): Record<string, unknown> | null => {
        const text = textOf(it);
        if (text === undefined || isStatusToken(text)) return null;
        const o = obj(it);
        // bet_type is the TYPE (mirrors assumption_type) — surfaced in the type pill.
        const type =
          typeof o.type === 'string' ? o.type : typeof o.bet_type === 'string' ? o.bet_type : undefined;
        // provenance/rationale is the ORIGIN — kept distinct from the type pill.
        const provenance =
          typeof o.provenance === 'string'
            ? o.provenance
            : typeof o.rationale === 'string'
              ? o.rationale
              : typeof o.tag === 'string'
                ? o.tag
                : undefined;
        return {
          text,
          ...(type !== undefined ? { type } : {}),
          ...(provenance !== undefined ? { provenance } : {}),
        };
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    return { ...data, ...draftPatch, payload: { kind: 'bets', items } };
  }

  if (p.kind === 'assumptions') {
    const mapItems = (items: unknown[]): Array<Record<string, unknown>> =>
      items
        .map((it): Record<string, unknown> | null => {
          const text = textOf(it);
          if (text === undefined || isStatusToken(text)) return null;
          const o = obj(it);
          const out: Record<string, unknown> = { text };
          // Type: card `type` or the typed-capture `assumption_type`.
          const type = typeof o.type === 'string' ? o.type : typeof o.assumption_type === 'string' ? o.assumption_type : undefined;
          if (type !== undefined) out.type = type;
          // Risk: card `risk` or the typed-capture `risk_level` (verbatim → strict enum).
          if (o.risk !== undefined) out.risk = o.risk;
          else if (o.risk_level !== undefined) out.risk = o.risk_level;
          if (o.weak !== undefined) out.weak = o.weak;
          return out;
        })
        .filter((x): x is Record<string, unknown> => x !== null);

    let groups: Array<{ bet: string; items: Array<Record<string, unknown>> }>;
    if (Array.isArray(p.groups)) {
      groups = list(p.groups).map((g) => {
        const go = obj(g);
        return { bet: typeof go.bet === 'string' ? go.bet : textOf(g) ?? '', items: mapItems(list(go.items)) };
      });
    } else if (Array.isArray(p.bets)) {
      // Embedded typed shape: bets[].assumptions[] → one group per parent bet.
      groups = list(p.bets).map((b) => ({ bet: textOf(b) ?? '', items: mapItems(list(obj(b).assumptions)) }));
    } else {
      // Flat typed list (no groups) → one unlabelled group.
      groups = [{ bet: '', items: mapItems(list(p.items)) }];
    }
    return { ...data, ...draftPatch, payload: { kind: 'assumptions', groups } };
  }

  if (p.kind === 'goals') {
    const items = list(p.items)
      .map((it): Record<string, unknown> | null => {
        // Legacy bare string, `{ text }`, or the typed `{ name, goal_type }` shape.
        const text = textOf(it) ?? (typeof obj(it).name === 'string' ? (obj(it).name as string) : undefined);
        if (text === undefined || text === '' || isStatusToken(text)) return null;
        const o = obj(it);
        const type = typeof o.type === 'string' ? o.type : typeof o.goal_type === 'string' ? o.goal_type : undefined;
        return { text, ...(type !== undefined ? { type } : {}) };
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    return { ...data, ...draftPatch, payload: { kind: 'goals', items } };
  }

  // Unified bets-with-assumptions (MF1). Cloud materialises this in card shape, so this
  // branch is a defensive typed→card coercion (statement→bet, bet_type→type, per-bet status,
  // nested assumptions[].{statement→text, assumption_type→type, risk_level→risk}). Empty
  // assumption arrays are PRESERVED (a bet shown before its assumptions — MF2).
  if (p.kind === 'bets_with_assumptions') {
    // ANA-936 — this rebuild runs on EVERY payload (it's the z.preprocess ahead of
    // the inner schema, even for already card-shaped input), so a stable roster
    // `id` present on the raw item must be explicitly carried through here or it
    // is silently dropped before the inner schema ever sees it.
    const mapAsm = (items: unknown[]): Array<Record<string, unknown>> =>
      items
        .map((it): Record<string, unknown> | null => {
          const text = textOf(it);
          if (text === undefined || isStatusToken(text)) return null;
          const o = obj(it);
          const out: Record<string, unknown> = { text };
          const type = typeof o.type === 'string' ? o.type : typeof o.assumption_type === 'string' ? o.assumption_type : undefined;
          if (type !== undefined) out.type = type;
          if (o.risk !== undefined) out.risk = o.risk;
          else if (o.risk_level !== undefined) out.risk = o.risk_level;
          if (o.weak !== undefined) out.weak = o.weak;
          if (typeof o.id === 'string') out.id = o.id;
          return out;
        })
        .filter((x): x is Record<string, unknown> => x !== null);
    const groups = list(p.groups).map((g) => {
      const go = obj(g);
      const bet = typeof go.bet === 'string' ? go.bet : textOf(g) ?? '';
      const type = typeof go.type === 'string' ? go.type : typeof go.bet_type === 'string' ? go.bet_type : undefined;
      const provenance = typeof go.provenance === 'string' ? go.provenance : typeof go.rationale === 'string' ? go.rationale : undefined;
      const status = go.status === 'confirmed' ? 'confirmed' : go.status === 'draft' ? 'draft' : undefined;
      const id = typeof go.id === 'string' ? go.id : undefined;
      return {
        ...(id !== undefined ? { id } : {}),
        bet,
        ...(type !== undefined ? { type } : {}),
        ...(provenance !== undefined ? { provenance } : {}),
        ...(status !== undefined ? { status } : {}),
        assumptions: mapAsm(list(go.assumptions ?? go.items)),
      };
    });
    return { ...data, ...draftPatch, payload: { kind: 'bets_with_assumptions', groups } };
  }

  return raw;
}

export const ScArtifactCardPropsSchema = z.preprocess(
  normalizeScArtifactData,
  z.object({
    payload: z.discriminatedUnion('kind', [
      ScArtifactDecisionPayload,
      ScArtifactBetsPayload,
      ScArtifactAssumptionsPayload,
      ScArtifactBetsWithAssumptionsPayload,
      ScArtifactGoalsPayload,
      ScArtifactScopePayload,
      ScArtifactNotePayload,
    ]),
    draft: z
      .boolean()
      .optional()
      .describe(
        "True while the item is still a working draft awaiting the user's approval; false once the user has confirmed it.",
      ),
    /**
     * 004-p10 (INV-12) — replaces the kind-derived header for a card whose content
     * did not come from the call itself (today: a decision seeded from project
     * creation, shown as "Our Starting Point" on entering frame).
     *
     * Deliberately absent from {@link scArtifactCardSchemaForKind}: that schema is
     * baked into the model's prompt layer as documentation, and this is
     * Cloud-set provenance, not something the model chooses.
     */
    titleOverride: z.string().max(60).optional(),
  }),
);
export type ScArtifactCardProps = z.infer<typeof ScArtifactCardPropsSchema>;

/** The `kind` discriminator values a `sc_artifact_card` payload can take. */
export const SC_ARTIFACT_KINDS = ['decision', 'bets', 'assumptions', 'bets_with_assumptions', 'goals', 'scope', 'note'] as const;
export type ScArtifactKind = (typeof SC_ARTIFACT_KINDS)[number];

/** Per-kind payload schema, for narrowing the `sc_artifact_card` union to a
 *  single kind (ANA-504 — bake only the section-relevant artifact variant). */
const SC_ARTIFACT_KIND_PAYLOAD: Record<ScArtifactKind, z.ZodType> = {
  decision: ScArtifactDecisionPayload,
  bets: ScArtifactBetsPayload,
  assumptions: ScArtifactAssumptionsPayload,
  bets_with_assumptions: ScArtifactBetsWithAssumptionsPayload,
  goals: ScArtifactGoalsPayload,
  scope: ScArtifactScopePayload,
  note: ScArtifactNotePayload,
};

/**
 * The `sc_artifact_card` schema narrowed to a single `kind`.
 *
 * Used by ANA-504 to bake only the section-relevant artifact variant into a
 * scoping section's prompt layer (vs the full 6-kind discriminated union).
 * Mirrors {@link ScArtifactCardPropsSchema} minus the inbound `z.preprocess`
 * (the baked schema is documentation for the model, not a parse gate) and minus
 * the union (one kind only). Field `.describe()`s flow through to the JSONSchema.
 */
export function scArtifactCardSchemaForKind(kind: ScArtifactKind): z.ZodType {
  return z.object({
    payload: SC_ARTIFACT_KIND_PAYLOAD[kind],
    draft: z
      .boolean()
      .optional()
      .describe(
        "True while the item is still a working draft awaiting the user's approval; false once the user has confirmed it.",
      ),
  });
}

/** Maps widget_type → Zod schema for render-time validation */
export const WIDGET_SCHEMA_CATALOG: Record<string, z.ZodType> = {
  [WIDGET_TYPES.INSIGHTS_SUMMARY]: InsightsSummaryPropsSchema,
  [WIDGET_TYPES.PROJECT_STATUS]: ProjectStatusPropsSchema,
  [WIDGET_TYPES.RECOMMENDATION]: RecommendationPropsSchema,
  [WIDGET_TYPES.PROJECT_CONFIDENCE]: ProjectConfidencePropsSchema,
  [WIDGET_TYPES.STAT_CARD]: StatCardPropsSchema,
  [WIDGET_TYPES.AGENDA_ITEM]: AgendaItemPropsSchema,
  [WIDGET_TYPES.PROJECT_PLAN_STEPPER]: ProjectPlanStepperPropsSchema,
  [WIDGET_TYPES.PROJECT_SNAPSHOT_CARD]: ProjectSnapshotCardPropsSchema,
  [WIDGET_TYPES.PROJECT_CARD]: ProjectCardPropsSchema,
  [WIDGET_TYPES.RESEARCH_CURRENT_PHASE_SNAPSHOT]: ResearchCurrentPhaseSnapshotPayloadSchema,
  [WIDGET_TYPES.RESEARCH_BETS_SNAPSHOT]: ResearchBetsSnapshotPayloadSchema,
  [WIDGET_TYPES.RESEARCH_SEED_AND_SIDE]: ResearchSeedAndSidePropsSchema,
  [WIDGET_TYPES.INTERVIEW_PLAYBACK]: InterviewPlaybackPropsSchema,
  [WIDGET_TYPES.BET_DETAIL]: BetDetailPropsSchema,
  // Phase A additions ── aggregator-backed widgets.
  [WIDGET_TYPES.PROJECT_HERO]: ProjectHeroPayloadSchema,
  [WIDGET_TYPES.PROJECT_RECENT_ACTIVITY]: ProjectRecentActivityPayloadSchema,
  [WIDGET_TYPES.FEATURED_RECOMMENDATION]: FeaturedRecommendationPayloadSchema,
  [WIDGET_TYPES.AGENDA_OPEN_ITEMS]: AgendaOpenItemsPayloadSchema,
  [WIDGET_TYPES.OVERVIEW_NEXT_INTERVIEW]: OverviewNextInterviewPayloadSchema,
  [WIDGET_TYPES.RESEARCH_SEED_SECTION]: ResearchSeedSectionPayloadSchema,
  [WIDGET_TYPES.RESEARCH_CAPTURED_SIGNALS]: ResearchCapturedSignalsPayloadSchema,
  [WIDGET_TYPES.RESEARCH_QUICK_ACTIONS]: ResearchQuickActionsPayloadSchema,
  [WIDGET_TYPES.INTERVIEWS_UPCOMING_LIST]: InterviewsUpcomingListPayloadSchema,
  [WIDGET_TYPES.INTERVIEWS_COMPLETED_LIST]: InterviewsCompletedListPayloadSchema,
  [WIDGET_TYPES.INSIGHTS_CONFIDENCE_TRAJECTORY]: InsightsConfidenceTrajectoryPayloadSchema,
  [WIDGET_TYPES.INSIGHTS_BETS_GRAPH]: InsightsBetsGraphPayloadSchema,
  [WIDGET_TYPES.RECOMMENDATIONS_LIST]: RecommendationsListPayloadSchema,
  [WIDGET_TYPES.PARTICIPANTS_ICP_PANEL]: ParticipantsIcpPanelPayloadSchema,
  [WIDGET_TYPES.PARTICIPANTS_TABLE]: ParticipantsTablePayloadSchema,
  [WIDGET_TYPES.MEETINGS_LIVE_CARD]: MeetingsLiveCardPayloadSchema,
  [WIDGET_TYPES.MEETINGS_UPCOMING_LIST]: MeetingsUpcomingListPayloadSchema,
  [WIDGET_TYPES.MEETINGS_COMPLETED_LIST]: MeetingsCompletedListPayloadSchema,
  [WIDGET_TYPES.MEETINGS_KPI_STRIP]: MeetingsKpiStripPayloadSchema,
  // 022 — Chat thread widgets
  [WIDGET_TYPES.BET_SNAPSHOT]: BetSnapshotPropsSchema,
  [WIDGET_TYPES.BETS_OVERVIEW]: BetsOverviewPropsSchema,
  [WIDGET_TYPES.ASSUMPTION_SNAPSHOT]: AssumptionSnapshotPropsSchema,
  [WIDGET_TYPES.DECISION_CARD]: DecisionCardPropsSchema,
  [WIDGET_TYPES.PROJECT_ACTION]: ProjectActionPropsSchema,
  [WIDGET_TYPES.RECOMMENDATIONS_CARD]: RecommendationsCardPropsSchema,
  [WIDGET_TYPES.INTERVIEWS_LIST]: InterviewsListPropsSchema,
  [WIDGET_TYPES.PROJECT_PLAN_SNAPSHOT]: ProjectPlanSnapshotPropsSchema,
  [WIDGET_TYPES.LINK_CARD]: LinkCardPropsSchema,
  // 026 — Scoping widgets
  [WIDGET_TYPES.SC_LEARNT_CARD]: ScLearntCardPropsSchema,
  [WIDGET_TYPES.SC_CHOICE_CARD]: ScChoiceCardPropsSchema,
  [WIDGET_TYPES.SC_PARTICIPANT_TARGETS_CARD]: ScParticipantTargetsCardPropsSchema,
  [WIDGET_TYPES.SC_RESEARCH_PREVIEW_CARD]: ScResearchPreviewCardPropsSchema,
  [WIDGET_TYPES.SC_SCOPE_SUMMARY_CARD]: ScScopeSummaryCardPropsSchema,
  [WIDGET_TYPES.SC_ARTIFACT_CARD]: ScArtifactCardPropsSchema,
  [WIDGET_TYPES.DEMO_SUMMARY_CARD]: DemoSummaryCardPropsSchema,
};

/**
 * Dashboards the aggregator endpoint can serve. The string union doubles as
 * the `:dashboardId` route segment and the `WIDGET_TO_DASHBOARD` value type.
 */
export const DASHBOARD_IDS = [
  'overview',
  'research',
  'interviews',
  'insights',
  'recommendations',
  'participants',
  'meetings',
  'bet_detail',
] as const;
export type DashboardId = (typeof DASHBOARD_IDS)[number];

/**
 * Maps each aggregator-addressable widget id to its owning dashboard. Phase A
 * registers every aggregator widget here — chat / MCP / hooks resolve a single
 * widget back to a dashboard via this table.
 *
 * **Type is `Partial`**: legacy chat widget ids (`insights_summary`,
 * `project_status`, etc.) are NOT included — they have no aggregator-backed
 * dashboard and the server route never serves them. Callers that look up a
 * widget id must check for `undefined` and handle it (e.g. `useDashboardWidget`
 * throws immediately so the caller knows the id is invalid).
 *
 * Phase A aggregator widgets — the comment boundary below marks where the
 * aggregator-backed ids start. Everything above it is a pre-Phase-A V1 chat
 * widget that intentionally has no mapping.
 *
 * `bet_detail` is a drilldown dashboard (one widget = the page), preserved
 * for MCP `widget_resource(bet_detail)` consumers.
 */
export const WIDGET_TO_DASHBOARD: Partial<Record<WidgetId, DashboardId>> = {
  // ── Phase A aggregator-backed widgets ─────────────────────────────────────
  // Overview
  [WIDGET_TYPES.PROJECT_HERO]: 'overview',
  [WIDGET_TYPES.PROJECT_PLAN_STEPPER]: 'overview',
  [WIDGET_TYPES.PROJECT_RECENT_ACTIVITY]: 'overview',
  [WIDGET_TYPES.FEATURED_RECOMMENDATION]: 'overview',
  [WIDGET_TYPES.AGENDA_OPEN_ITEMS]: 'overview',
  [WIDGET_TYPES.OVERVIEW_NEXT_INTERVIEW]: 'overview',
  // Research
  [WIDGET_TYPES.RESEARCH_CURRENT_PHASE_SNAPSHOT]: 'research',
  [WIDGET_TYPES.RESEARCH_BETS_SNAPSHOT]: 'research',
  [WIDGET_TYPES.RESEARCH_SEED_SECTION]: 'research',
  [WIDGET_TYPES.RESEARCH_CAPTURED_SIGNALS]: 'research',
  [WIDGET_TYPES.RESEARCH_QUICK_ACTIONS]: 'research',
  // WIDGET_TYPES.RESEARCH_SEED_AND_SIDE intentionally absent — @deprecated, replaced by
  // research_seed_section + research_captured_signals (PL-260517 Phase A).
  // Interviews
  [WIDGET_TYPES.INTERVIEWS_UPCOMING_LIST]: 'interviews',
  [WIDGET_TYPES.INTERVIEWS_COMPLETED_LIST]: 'interviews',
  // WIDGET_TYPES.INTERVIEW_PLAYBACK intentionally absent — DEFERRED until n8n PR-6 ships (plan §6).
  // Insights
  [WIDGET_TYPES.INSIGHTS_CONFIDENCE_TRAJECTORY]: 'insights',
  [WIDGET_TYPES.INSIGHTS_BETS_GRAPH]: 'insights',
  // Recommendations
  [WIDGET_TYPES.RECOMMENDATIONS_LIST]: 'recommendations',
  // Participants
  [WIDGET_TYPES.PARTICIPANTS_ICP_PANEL]: 'participants',
  [WIDGET_TYPES.PARTICIPANTS_TABLE]: 'participants',
  // Meetings
  [WIDGET_TYPES.MEETINGS_LIVE_CARD]: 'meetings',
  [WIDGET_TYPES.MEETINGS_UPCOMING_LIST]: 'meetings',
  [WIDGET_TYPES.MEETINGS_COMPLETED_LIST]: 'meetings',
  [WIDGET_TYPES.MEETINGS_KPI_STRIP]: 'meetings',
  // Bet detail (drilldown)
  [WIDGET_TYPES.BET_DETAIL]: 'bet_detail',
  // Legacy pre-Phase-A V1 chat widgets are intentionally absent — they have
  // no aggregator-backed endpoint. `useDashboardWidget` throws at call time
  // if a caller attempts to look one up.
};

/**
 * Widget ids exposed in the MCP `widget_catalog` tool — the subset used in
 * chat threads. All are LLM-emitted: the model constructs the payload from
 * conversation context and embeds a WidgetBlock directly in its response.
 *
 * Dashboard/aggregator widgets (WIDGET_TO_DASHBOARD) and legacy V1 ids are
 * intentionally excluded — they are not rendered in the chat thread.
 */
export const CHAT_THREAD_WIDGET_IDS: readonly WidgetId[] = [
  // 022 — Chat thread widgets
  WIDGET_TYPES.BET_SNAPSHOT,
  WIDGET_TYPES.BETS_OVERVIEW,
  WIDGET_TYPES.ASSUMPTION_SNAPSHOT,
  WIDGET_TYPES.DECISION_CARD,
  WIDGET_TYPES.PROJECT_ACTION,
  WIDGET_TYPES.RECOMMENDATIONS_CARD,
  WIDGET_TYPES.INTERVIEWS_LIST,
  WIDGET_TYPES.PROJECT_PLAN_SNAPSHOT,
  WIDGET_TYPES.LINK_CARD,
  // 026 — Scoping widgets
  WIDGET_TYPES.SC_LEARNT_CARD,
  WIDGET_TYPES.SC_CHOICE_CARD,
  WIDGET_TYPES.SC_PARTICIPANT_TARGETS_CARD,
  WIDGET_TYPES.SC_RESEARCH_PREVIEW_CARD,
  WIDGET_TYPES.SC_SCOPE_SUMMARY_CARD,
  WIDGET_TYPES.SC_ARTIFACT_CARD,
] as const;
